import React, { useState, useEffect } from 'react'
import {
  Shield,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Clock,
  Activity,
  TrendingUp,
  Calendar,
  ExternalLink,
  Bell,
  RefreshCw
} from 'lucide-react'

interface StatusComponent {
  name: string
  status: 'operational' | 'degraded' | 'partial_outage' | 'major_outage'
  description?: string
  uptime?: string
}

interface Incident {
  id: string
  title: string
  status: 'investigating' | 'identified' | 'monitoring' | 'resolved'
  severity: 'low' | 'medium' | 'high' | 'critical'
  created: string
  updated: string
  description: string
  affectedComponents: string[]
  updates: {
    timestamp: string
    status: string
    message: string
  }[]
}

interface Metric {
  name: string
  value: string
  unit: string
  status: 'good' | 'warning' | 'critical'
  trend: 'up' | 'down' | 'stable'
}

const StatusPage = () => {
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date())
  const [autoRefresh, setAutoRefresh] = useState(true)

  const systemStatus = 'operational' // Could be: operational, degraded, partial_outage, major_outage

  const components: StatusComponent[] = [
    { name: 'API Services', status: 'operational', uptime: '99.98%' },
    { name: 'Web Application', status: 'operational', uptime: '99.99%' },
    { name: 'Database', status: 'operational', uptime: '99.97%' },
    { name: 'File Storage', status: 'operational', uptime: '99.96%' },
    { name: 'Email Delivery', status: 'degraded', uptime: '98.45%', description: 'Experiencing delays in email delivery' },
    { name: 'Authentication', status: 'operational', uptime: '99.99%' },
    { name: 'Search Services', status: 'operational', uptime: '99.95%' },
    { name: 'Payment Processing', status: 'operational', uptime: '99.98%' }
  ]

  const currentIncidents: Incident[] = [
    {
      id: '1',
      title: 'Email Delivery Delays',
      status: 'monitoring',
      severity: 'medium',
      created: '2024-01-15T14:30:00Z',
      updated: '2024-01-15T16:45:00Z',
      description: 'Some users may experience delays in receiving email notifications. We are working with our email service provider to resolve this issue.',
      affectedComponents: ['Email Delivery'],
      updates: [
        {
          timestamp: '2024-01-15T16:45:00Z',
          status: 'monitoring',
          message: 'Email delivery has been restored. We are monitoring the situation to ensure stability.'
        },
        {
          timestamp: '2024-01-15T15:20:00Z',
          status: 'identified',
          message: 'We have identified the root cause as a configuration issue with our email service provider and are implementing a fix.'
        },
        {
          timestamp: '2024-01-15T14:30:00Z',
          status: 'investigating',
          message: 'We are investigating reports of delayed email notifications.'
        }
      ]
    }
  ]

  const recentIncidents: Incident[] = [
    {
      id: '2',
      title: 'Database Connection Issues',
      status: 'resolved',
      severity: 'high',
      created: '2024-01-10T09:15:00Z',
      updated: '2024-01-10T10:30:00Z',
      description: 'Users experienced intermittent issues accessing the application due to database connectivity problems.',
      affectedComponents: ['Database', 'Web Application'],
      updates: [
        {
          timestamp: '2024-01-10T10:30:00Z',
          status: 'resolved',
          message: 'All systems are now fully operational. We have implemented additional monitoring to prevent similar issues.'
        },
        {
          timestamp: '2024-01-10T09:45:00Z',
          status: 'monitoring',
          message: 'Database connectivity has been restored. We are monitoring the system closely.'
        },
        {
          timestamp: '2024-01-10T09:15:00Z',
          status: 'investigating',
          message: 'We are investigating reports of application access issues.'
        }
      ]
    }
  ]

  const metrics: Metric[] = [
    { name: 'Response Time', value: '142', unit: 'ms', status: 'good', trend: 'stable' },
    { name: 'Error Rate', value: '0.03', unit: '%', status: 'good', trend: 'down' },
    { name: 'Throughput', value: '2,340', unit: 'req/min', status: 'good', trend: 'up' },
    { name: 'Availability', value: '99.98', unit: '%', status: 'good', trend: 'stable' }
  ]

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'operational':
        return 'text-green-600 bg-green-100'
      case 'degraded':
        return 'text-yellow-600 bg-yellow-100'
      case 'partial_outage':
        return 'text-orange-600 bg-orange-100'
      case 'major_outage':
        return 'text-red-600 bg-red-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'operational':
        return <CheckCircle2 className="h-4 w-4" />
      case 'degraded':
        return <AlertTriangle className="h-4 w-4" />
      case 'partial_outage':
      case 'major_outage':
        return <XCircle className="h-4 w-4" />
      default:
        return <Clock className="h-4 w-4" />
    }
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'low':
        return 'text-blue-600 bg-blue-100'
      case 'medium':
        return 'text-yellow-600 bg-yellow-100'
      case 'high':
        return 'text-orange-600 bg-orange-100'
      case 'critical':
        return 'text-red-600 bg-red-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const getIncidentStatusColor = (status: string) => {
    switch (status) {
      case 'resolved':
        return 'text-green-600 bg-green-100'
      case 'monitoring':
        return 'text-blue-600 bg-blue-100'
      case 'identified':
        return 'text-yellow-600 bg-yellow-100'
      case 'investigating':
        return 'text-orange-600 bg-orange-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const getMetricStatusColor = (status: string) => {
    switch (status) {
      case 'good':
        return 'text-green-600'
      case 'warning':
        return 'text-yellow-600'
      case 'critical':
        return 'text-red-600'
      default:
        return 'text-gray-600'
    }
  }

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="h-3 w-3 text-green-600" />
      case 'down':
        return <TrendingUp className="h-3 w-3 text-red-600 transform rotate-180" />
      default:
        return <Activity className="h-3 w-3 text-gray-600" />
    }
  }

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        setLastUpdated(new Date())
      }, 30000) // Refresh every 30 seconds

      return () => clearInterval(interval)
    }
  }, [autoRefresh])

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <Shield className="h-8 w-8 text-primary-600" />
              <h1 className="ml-3 text-2xl font-bold text-gray-900">System Status</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                  autoRefresh ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                Auto-refresh
              </button>
              <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                <Bell className="h-4 w-4 mr-2" />
                Subscribe
              </button>
              <a href="/" className="text-primary-600 hover:text-primary-700">← Back to Trust Center</a>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overall Status */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`inline-flex items-center px-4 py-2 rounded-full text-lg font-medium ${getStatusColor(systemStatus)}`}>
                {getStatusIcon(systemStatus)}
                <span className="ml-3 capitalize">All Systems {systemStatus}</span>
              </div>
            </div>
            <div className="text-sm text-gray-500">
              Last updated: {lastUpdated.toLocaleString()}
            </div>
          </div>
        </div>

        {/* Current Incidents */}
        {currentIncidents.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Current Incidents</h2>
            <div className="space-y-4">
              {currentIncidents.map((incident) => (
                <div key={incident.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <h3 className="text-lg font-semibold text-gray-900 mr-3">{incident.title}</h3>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getIncidentStatusColor(incident.status)}`}>
                          {incident.status.replace('_', ' ')}
                        </span>
                        <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(incident.severity)}`}>
                          {incident.severity} severity
                        </span>
                      </div>
                      <p className="text-gray-600 mb-4">{incident.description}</p>
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="h-4 w-4 mr-1" />
                        Started: {new Date(incident.created).toLocaleString()}
                        <span className="mx-2">•</span>
                        Last update: {new Date(incident.updated).toLocaleString()}
                      </div>
                    </div>
                  </div>

                  {/* Incident Updates */}
                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Updates</h4>
                    <div className="space-y-3">
                      {incident.updates.map((update, index) => (
                        <div key={index} className="flex items-start">
                          <div className="flex-shrink-0">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getIncidentStatusColor(update.status)}`}>
                              {getStatusIcon(update.status)}
                            </div>
                          </div>
                          <div className="ml-3 flex-1">
                            <div className="flex items-center">
                              <span className="text-sm font-medium text-gray-900 capitalize">
                                {update.status.replace('_', ' ')}
                              </span>
                              <span className="ml-2 text-sm text-gray-500">
                                {new Date(update.timestamp).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{update.message}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Performance Metrics */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Performance Metrics</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {metrics.map((metric, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-900">{metric.name}</h3>
                  {getTrendIcon(metric.trend)}
                </div>
                <div className="flex items-baseline">
                  <span className={`text-2xl font-bold ${getMetricStatusColor(metric.status)}`}>
                    {metric.value}
                  </span>
                  <span className="ml-1 text-sm text-gray-500">{metric.unit}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* System Components */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">System Components</h2>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="divide-y divide-gray-200">
              {components.map((component, index) => (
                <div key={index} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <h3 className="text-sm font-medium text-gray-900">{component.name}</h3>
                        {component.description && (
                          <span className="ml-2 text-sm text-gray-500">— {component.description}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      {component.uptime && (
                        <span className="text-sm text-gray-500">{component.uptime}</span>
                      )}
                      <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(component.status)}`}>
                        {getStatusIcon(component.status)}
                        <span className="ml-2 capitalize">{component.status.replace('_', ' ')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Incidents */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Incidents</h2>
          <div className="space-y-4">
            {recentIncidents.map((incident) => (
              <div key={incident.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 mr-3">{incident.title}</h3>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getIncidentStatusColor(incident.status)}`}>
                        {incident.status}
                      </span>
                      <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(incident.severity)}`}>
                        {incident.severity}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-2">{incident.description}</p>
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="h-4 w-4 mr-1" />
                      {new Date(incident.created).toLocaleDateString()} - {new Date(incident.updated).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-blue-50 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Stay Informed</h3>
              <p className="text-gray-600">
                Subscribe to status updates to receive notifications about incidents and maintenance windows.
              </p>
            </div>
            <div className="flex space-x-4">
              <button className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700">
                <Bell className="h-4 w-4 mr-2" />
                Email Alerts
              </button>
              <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                <ExternalLink className="h-4 w-4 mr-2" />
                RSS Feed
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default StatusPage